import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN, ReminderFrequency, Patient, TranslationParameter } from '@pxl/models/common';
import { DosageByWeight, FormatService, LoginService, MayBaseDiagnosis, MayBaseService, UserFrequency } from '@pxl/models/contracts';
import {
    MayBaseDosageByWeightModel,
    MayBaseProductModel,
    MayBaseProductUnitModel,
    MayBaseRouteOfAdministrationModel,
    MayBaseService as MayBaseBackendService,
    WeightModel,
} from '@pxl/shared/generated';
import { LanguageService } from '@pxl/shared/translation';
import { hasValue, sortBy } from '@pxl/shared/util';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, delay, map, take, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class DefaultMayBaseService implements MayBaseService {
    private readonly ApiParams: { API_VERSION: string; TOKEN: string } = null;
    private patient?: Patient;

    private readonly mayBaseProduct$$ = new ReplaySubject<MayBaseProductModel>(1);
    private readonly diagnoses$$ = new ReplaySubject<MayBaseDiagnosis[]>(1);
    private readonly dosagesByWeight$$ = new BehaviorSubject<DosageByWeight[]>([]);
    private readonly numberOfUnitsPerApplication$$ = new Subject<number | undefined>();

    private readonly selectedDiagnosis$$ = new ReplaySubject<MayBaseDiagnosis | undefined>(1);
    private readonly selectedDosageByWeight$$ = new ReplaySubject<string | undefined>(1);

    public readonly mayBaseProduct$ = this.mayBaseProduct$$.asObservable();
    public readonly dosagesByWeight$ = this.dosagesByWeight$$.asObservable();
    public readonly diagnoses$: Observable<MayBaseDiagnosis[]> = this.diagnoses$$.asObservable();

    public readonly selectedDiagnosis$: Observable<MayBaseDiagnosis | undefined> = this.selectedDiagnosis$$.asObservable();
    public readonly selectedDosageByWeight$: Observable<string | undefined> = this.selectedDosageByWeight$$.asObservable();

    /**
    selectDiagnosis$ => Triggers:
                        numberOfApplicationUnits$
                        applicationUnit$
                        routeOfAdministration$
                        repeatAfterInHours$

    selectDosageByWeight$ => Triggers:
                                numberOfUnitsPerApplication$
    */
    public readonly applicationUnit$: Observable<MayBaseProductUnitModel> = this.selectedDiagnosis$.pipe(
        withLatestFrom(this.mayBaseProduct$),
        map(([, mayBaseProduct]) => mayBaseProduct?.applicationUnit),
    );
    public readonly numberOfApplicationUnits$: Observable<number | undefined> = this.selectedDiagnosis$.pipe(
        withLatestFrom(this.mayBaseProduct$),
        map(([, mayBaseProduct]) => mayBaseProduct?.numberOfApplicationUnits),
    );
    public readonly routeOfAdministration$: Observable<MayBaseRouteOfAdministrationModel | undefined> = this.selectedDiagnosis$.pipe(
        map((selectedDiagnosis) => selectedDiagnosis?.dosage?.routeOfAdministration),
    );
    public readonly repeatAfterInHours$: Observable<number | undefined> = this.selectedDiagnosis$.pipe(
        map((selectedDiagnosis) => selectedDiagnosis?.dosage?.repeatAfterInHours),
    );
    public readonly numberOfUnitsPerApplication$ = this.numberOfUnitsPerApplication$$.asObservable();

    private readonly config = inject(CONFIG_TOKEN);
    private readonly mayBaseBackendService = inject(MayBaseBackendService);
    private readonly formatService = inject(FormatService);
    private readonly languageService = inject(LanguageService);
    private readonly loginService = inject(LoginService);
    public constructor() {
        this.ApiParams = {
            API_VERSION: this.config.API_VERSION,
            TOKEN: this.loginService.getToken(),
        };

        // Subscriptions: Caching values in ReplaySubjects.
        this.mayBaseProduct$.subscribe((mayBaseProduct) => {
            let dosages = (mayBaseProduct?.dosages ?? []).filter((dosage) => dosage.preventionStatus);

            if (this.patient?.speciesId) {
                const { speciesId } = this.patient;
                dosages = dosages.filter((dosage) => dosage.species?.id === speciesId);
            }

            const diagnoses = dosages.map((dosage) => {
                return {
                    dosageId: dosage.id,
                    diagnosisId: dosage.diagnosis.id,
                    speciesId: dosage.species.id,

                    dosage: dosage,
                    diagnosis: dosage.diagnosis,
                    species: dosage.species,
                } as MayBaseDiagnosis;
            });

            this.diagnoses$$.next(diagnoses);
        });

        this.selectedDiagnosis$
            .pipe(
                map((selectedDiagnosis) => {
                    const { specificDosageInfos } = selectedDiagnosis?.dosage ?? {
                        specificDosageInfos: [] as MayBaseDosageByWeightModel[],
                    };

                    const dosagesByWeight: DosageByWeight[] =
                        specificDosageInfos?.map((specificDosageInfo) => {
                            return {
                                mayBaseDosageByWeightModel: specificDosageInfo,
                                displayText: `${this.formatService.formatNumber(
                                    specificDosageInfo.weightRangeStartInKg,
                                    '1.0-3',
                                )} - ${this.formatService.formatNumber(specificDosageInfo.weightRangeEndInKg, '1.0-3')} kg`,
                            };
                        }) ?? [];

                    return sortBy(dosagesByWeight, (dosageByWeight) => dosageByWeight.mayBaseDosageByWeightModel.weightRangeStartInKg);
                }),
            )
            .subscribe((dosagesByWeight) => {
                this.dosagesByWeight$$.next(dosagesByWeight);
                if (!dosagesByWeight?.length) {
                    this.numberOfUnitsPerApplication$$.next(undefined);
                }
            });

        this.selectedDosageByWeight$$
            .pipe(delay(1), withLatestFrom(this.selectedDiagnosis$))
            .subscribe(([dosageByWeightId, selectedDiagnosis]) => {
                const { specificDosageInfos: dosagesByWeight } = selectedDiagnosis?.dosage ?? { specificDosageInfos: undefined };
                if (dosagesByWeight) {
                    const dosageByWeight = dosagesByWeight.find((dosageByWeight) => dosageByWeight.id === dosageByWeightId);
                    this.numberOfUnitsPerApplication$$.next(dosageByWeight?.numberOfUnits ?? undefined);
                }
            });
    }

    public loadMayBaseProduct(productId: string, patient?: Patient): Observable<MayBaseProductModel> {
        this.patient = patient;

        this.mayBaseBackendService
            .getProduct(productId, this.ApiParams.API_VERSION, this.ApiParams.TOKEN)
            .pipe(
                take(1),
                catchError(() => {
                    return of(null);
                }),
            )
            .subscribe((product) => this.mayBaseProduct$$.next(product));

        return this.mayBaseProduct$;
    }

    public setMayBaseProduct(product: MayBaseProductModel): void {
        this.mayBaseProduct$$.next(product);
    }

    public isPatientWeightInRange(dosageByWeight: DosageByWeight, patientWeightModel: WeightModel | null | undefined): boolean {
        const { value: patientWeight } = patientWeightModel ?? { value: undefined };
        const { weightRangeEndInKg, weightRangeStartInKg } = dosageByWeight.mayBaseDosageByWeightModel;

        if (!hasValue(patientWeight) || !hasValue(weightRangeStartInKg) || !hasValue(weightRangeEndInKg)) return false;

        return patientWeight && patientWeight >= weightRangeStartInKg && patientWeight <= weightRangeEndInKg;
    }

    public selectDiagnosis(diagnosisId: string | undefined): void {
        this.diagnoses$.pipe(take(1)).subscribe((diagnoses) => {
            this.selectedDiagnosis$$.next(diagnosisId ? diagnoses.find((diagnosis) => diagnosis.diagnosisId === diagnosisId) : undefined);
        });
    }

    public selectDosageByWeight(dosageByWeightId: string | undefined): void {
        this.selectedDosageByWeight$$.next(dosageByWeightId);
    }

    public getUserFrequency(repeatAfterInHours: number): UserFrequency {
        if (repeatAfterInHours < 24) {
            // 1 DAY
            return {
                frequency: '1',
                frequencyUnit: ReminderFrequency.Day,
                remindersCount: Math.floor(24 / repeatAfterInHours),
            };
        } else if (repeatAfterInHours < 24 * 7) {
            // DAYS
            return {
                frequency: this.formatService.formatNumber(repeatAfterInHours / 24, '1.0-2'),
                frequencyUnit: ReminderFrequency.Day,
                remindersCount: null,
            };
        } else {
            // WEEKS
            return {
                frequency: this.formatService.formatNumber(repeatAfterInHours / 24 / 7, '1.0-2'),
                frequencyUnit: ReminderFrequency.Week,
                remindersCount: null,
            };
        }
    }

    public getNoSpeciesWarningMessageTranslationParameters(patient: Patient): TranslationParameter[] {
        return [
            {
                name: 'PATIENT_SPECIES',
                text: patient.speciesResourceKey ? this.languageService.translate(patient.speciesResourceKey) : patient.speciesText,
            },
            {
                name: 'PATIENT_NAME',
                text: patient.patientName,
            },
        ];
    }

    public hasPatientSpeciesSpecificDiagnoses(patient: Patient): Observable<boolean> {
        return this.diagnoses$.pipe(
            map((diagnoses) => {
                return diagnoses.filter((diagnosis) => diagnosis.speciesId === patient.speciesId);
            }),
            map((dosages) => dosages.length > 0),
            take(1),
        );
    }
}
